import { useState } from 'react'
import Card from '../ui/Card'
import { Input, Badge } from '../ui'
import { Info } from 'lucide-react'
import type { Vendor } from '../../lib/services'
import { vendorsAPI } from '../../lib/services'

interface ComplianceProfileCardProps {
  vendor: Vendor
  onRefresh: () => void
}

interface ComplianceField {
  key: keyof Vendor
  label: string
  type: 'boolean' | 'text' | 'select'
  options?: string[]
}

const COMPLIANCE_FIELDS: ComplianceField[] = [
  { key: 'is_small_business', label: 'Small Business', type: 'boolean' },
  { key: 'is_8a_certified', label: '8(a) Certified', type: 'boolean' },
  { key: 'is_veteran_owned', label: 'Veteran-Owned (SDVOSB)', type: 'boolean' },
  { key: 'is_woman_owned', label: 'Woman-Owned (WOSB)', type: 'boolean' },
  { key: 'is_hubzone', label: 'HUBZone', type: 'boolean' },
  { key: 'has_foreign_ownership', label: 'Foreign Ownership (FOCI)', type: 'boolean' },
  { key: 'section_889_compliant', label: 'Section 889 Compliant', type: 'boolean' },
  { key: 'has_active_proceedings', label: 'Active Proceedings', type: 'boolean' },
  { key: 'is_disaster_response_contractor', label: 'Disaster Response', type: 'boolean' },
  { key: 'debarment_status', label: 'Debarment Status', type: 'select', options: ['active', 'debarred', 'suspended', 'unknown'] },
  { key: 'company_security_level', label: 'Facility Clearance', type: 'text' },
  { key: 'highest_employee_security_level', label: 'Personnel Clearance', type: 'text' },
  { key: 'annual_revenue_range', label: 'Revenue Range', type: 'text' },
]

function BooleanToggle({ value, onChange }: { value: boolean | null | undefined; onChange: (v: boolean | null) => void }) {
  if (value === null || value === undefined) {
    return (
      <div className="flex items-center gap-1">
        <button
          onClick={() => onChange(true)}
          className="px-2.5 py-1 text-xs rounded-l-md border border-border text-muted-foreground hover:bg-muted transition-colors"
        >
          Yes
        </button>
        <button
          onClick={() => onChange(false)}
          className="px-2.5 py-1 text-xs rounded-r-md border border-border text-muted-foreground hover:bg-muted transition-colors"
        >
          No
        </button>
      </div>
    )
  }
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => onChange(value === true ? null : true)}
        className={`px-2.5 py-1 text-xs rounded-l-md border transition-colors ${
          value === true
            ? 'bg-green-500/15 border-green-500/30 text-green-600 font-medium'
            : 'bg-transparent border-border text-muted-foreground hover:bg-muted'
        }`}
      >
        Yes
      </button>
      <button
        onClick={() => onChange(value === false ? null : false)}
        className={`px-2.5 py-1 text-xs rounded-r-md border transition-colors ${
          value === false
            ? 'bg-red-500/15 border-red-500/30 text-red-600 font-medium'
            : 'bg-transparent border-border text-muted-foreground hover:bg-muted'
        }`}
      >
        No
      </button>
    </div>
  )
}

function InlineSelectField({ value, options, onChange }: {
  value: string | undefined | null
  options: string[]
  onChange: (v: string) => void
}) {
  return (
    <select
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      className="h-7 px-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
    >
      <option value="">Select...</option>
      {options.map(o => <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>)}
    </select>
  )
}

function InlineTextField({ value, vendorId, fieldKey, onSaved }: {
  value: any
  vendorId: number
  fieldKey: keyof Vendor
  onSaved: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState('')
  const [saving, setSaving] = useState(false)

  const startEditing = () => {
    setEditValue(value != null ? String(value) : '')
    setEditing(true)
  }

  const save = async () => {
    if (editValue === (value != null ? String(value) : '')) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      await vendorsAPI.update(vendorId, { [fieldKey]: editValue } as any)
      onSaved()
    } catch (err) {
      console.error('Failed to save:', err)
    } finally {
      setSaving(false)
      setEditing(false)
    }
  }

  if (editing) {
    return (
      <Input
        autoFocus
        value={editValue}
        onChange={e => setEditValue(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false) }}
        className="h-7 text-sm w-48"
        disabled={saving}
      />
    )
  }

  return (
    <button onClick={startEditing} className="text-left group/field">
      {value ? (
        <span className="text-sm text-foreground group-hover/field:text-primary transition-colors">{String(value)}</span>
      ) : (
        <span className="text-sm text-muted-foreground/50 italic group-hover/field:text-primary transition-colors">Click to add</span>
      )}
    </button>
  )
}

const DEFAULT_VISIBLE = 6

export default function ComplianceProfileCard({ vendor, onRefresh }: ComplianceProfileCardProps) {
  const [expanded, setExpanded] = useState(false)

  const filledCount = COMPLIANCE_FIELDS.filter(f => {
    const val = vendor[f.key]
    return val !== null && val !== undefined && val !== ''
  }).length
  const completeness = Math.round((filledCount / COMPLIANCE_FIELDS.length) * 100)

  const displayFields = expanded ? COMPLIANCE_FIELDS : COMPLIANCE_FIELDS.slice(0, DEFAULT_VISIBLE)
  const hiddenCount = COMPLIANCE_FIELDS.length - DEFAULT_VISIBLE

  const handleBoolChange = async (key: keyof Vendor, value: boolean | null) => {
    try {
      await vendorsAPI.update(vendor.id, { [key]: value } as any)
      onRefresh()
    } catch (err) {
      console.error('Failed to save:', err)
    }
  }

  const handleSelectChange = async (key: keyof Vendor, value: string) => {
    try {
      await vendorsAPI.update(vendor.id, { [key]: value } as any)
      onRefresh()
    } catch (err) {
      console.error('Failed to save:', err)
    }
  }

  return (
    <Card className="p-6">
      <div className="mb-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <h3 className="text-base font-semibold text-foreground">Compliance Profile</h3>
            <div className="relative group/tip">
              <Info className="w-3.5 h-3.5 text-muted-foreground/50 cursor-help" />
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-56 px-3 py-2 text-xs text-foreground bg-popover border border-border rounded-lg shadow-lg opacity-0 pointer-events-none group-hover/tip:opacity-100 group-hover/tip:pointer-events-auto transition-opacity z-50">
                Details are inferred from public databases, web research, and uploaded documents. Click any field to edit, or upload documents at the bottom of this page to auto-fill details.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 rounded-full transition-all"
                style={{ width: `${completeness}%` }}
              />
            </div>
            <span className="text-xs text-muted-foreground">{completeness}%</span>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {displayFields.map(f => (
          <div key={f.key} className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground shrink-0 select-none w-36">
              {f.label}
            </span>
            <div className="flex-1 min-w-0">
              {f.type === 'boolean' ? (
                <BooleanToggle
                  value={vendor[f.key] as boolean | null}
                  onChange={(v) => handleBoolChange(f.key, v)}
                />
              ) : f.type === 'select' ? (
                <InlineSelectField
                  value={vendor[f.key] as string | null}
                  options={f.options || []}
                  onChange={(v) => handleSelectChange(f.key, v)}
                />
              ) : (
                <InlineTextField
                  value={vendor[f.key]}
                  vendorId={vendor.id}
                  fieldKey={f.key}
                  onSaved={onRefresh}
                />
              )}
            </div>
          </div>
        ))}
      </div>

      {hiddenCount > 0 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-blue-500 hover:text-blue-600 mt-3"
        >
          {expanded ? 'Show less' : `... ${hiddenCount} more`}
        </button>
      )}

      {vendor.consortium_memberships && Array.isArray(vendor.consortium_memberships) && vendor.consortium_memberships.length > 0 && (
        <div className="mt-5 pt-4 border-t border-border">
          <span className="text-xs text-muted-foreground">Consortium Memberships</span>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {vendor.consortium_memberships.map((c: string, i: number) => (
              <Badge key={i} variant="info" className="text-xs">{c}</Badge>
            ))}
          </div>
        </div>
      )}
    </Card>
  )
}
